import React, { useState } from 'react';
import './PresetChangePopup.css';
import axios from 'axios';
import { BYLAW_BASE_URL } from '../../api/services';

const PresetChangePopup = ({ 
  isOpen, 
  onClose, 
  onSave, 
  irisSelection, 
  userSelection, 
  irisExplanation,
  originalPresetId,
  modifiedPresetId,
  propertyId,
  category
}) => {
  const [userExplanation, setUserExplanation] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null, 'success', 'error'
  const [validationError, setValidationError] = useState(null);
  const [attemptCount, setAttemptCount] = useState(1);
  const MAX_ATTEMPTS = 5;
  const safeIrisSelection = irisSelection || 'Not available';
  const safeUserSelection = userSelection || 'Not available';
  const safeIrisExplanation = irisExplanation || 'Explanation not available';

  if (!isOpen) return null;

  const handleTextChange = (e) => {
    setUserExplanation(e.target.value);
    // Clear validation errors when user starts typing
    if (validationError) {
      setValidationError(null);
    }
  };

  const validateExplanation = async (trimmedExplanation) => {
    // Empty field validation
    if (!trimmedExplanation) {
      setValidationError({
        type: 'empty',
        message: "This field can't be blank."
      });
      return false;
    }

    // Call the validation API
    try {
      setIsProcessing(true);
      
      // API request to validate the explanation
      const config = {
        headers: {
          Authorization: "Bearer abcd",
          "Content-Type": "application/json",
        },
        method: "POST",
        data: JSON.stringify({
          property_id: propertyId,
          bylaw_category: category,
          original_preset_id: originalPresetId,
          modified_preset_id: modifiedPresetId,
          original_preset: irisSelection,  
          modified_preset: userSelection,  
          iris_explanation: irisExplanation,  
          user_explanation: trimmedExplanation,
          attempt_count: attemptCount
        }),
        mode: "no-cors",
      };
      
      const response = await axios(`${BYLAW_BASE_URL}/validate_bylaw_change`, config);
      const result = response.data;
      
      // Update attempt count for next try
      setAttemptCount(prevCount => prevCount + 1);
      
      // Check if the explanation is approved
      if (!result.is_approved && attemptCount < MAX_ATTEMPTS) {
        // Set validation error with LLM explanation
        setValidationError({
          type: 'llm_rejection',
          message: result.explanation
        });
        
        return false;
      }
      
      // If approved or it's the 5th attempt, consider it valid
      return true;
    } catch (error) {
      console.error("Error validating explanation:", error);
      
      // On API error, still increment attempt but allow submission on 5th try
      if (attemptCount >= MAX_ATTEMPTS) {
        return true;
      }
      
      setValidationError({
        type: 'api_error',
        message: "An error occurred while validating your explanation. Please try again."
      });
      
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSave = async () => {
    // Trim the text before validation
    const trimmedExplanation = userExplanation.trim();
    
    const shouldSkipValidation = (
      !originalPresetId ||
      !modifiedPresetId ||
      !irisSelection ||
      !irisExplanation
    );
    
    if (!shouldSkipValidation) {
      // Validate the explanation
      const isValid = await validateExplanation(trimmedExplanation);
      
      if (!isValid) {
        return;
      }
    }
    
    // If we reached here, the explanation is valid or it's the 5th attempt
    setIsProcessing(true);
    setSaveStatus(null);
    
    try {
      // Call the save function from props
      await onSave(trimmedExplanation);
      
      // Show success indicator
      if (attemptCount > MAX_ATTEMPTS) {
        setSaveStatus('force_success');
      } else {
        setSaveStatus('success');
      }
      
      // Reset after successful save
      setTimeout(() => {
        setIsProcessing(false);
        setSaveStatus(null);
        setUserExplanation('');
        onClose();
      }, 1500);
    } catch (error) {
      setSaveStatus('error');
      setTimeout(() => {
        setIsProcessing(false);
        setSaveStatus(null);
      }, 1500);
    }
  };

  const handleCancel = () => {
    setUserExplanation('');
    setValidationError(null);
    setAttemptCount(1);
    onClose();
  };

  // Determine if the input field should have an error class
  const inputErrorClass = validationError ? 'input-error' : '';
  
  // Show attempt counter if there have been rejections
  const showAttemptCounter = attemptCount > 1;

  return (
    <div className="preset-change-popup-overlay">
      <div className="preset-change-popup">
        <div className="preset-change-popup-content">
          <h2>Preset Change Explanation</h2>
          
          <div className="preset-field">
            <h3 className="text-heading">Your Selection</h3>
            <div className="preset-value">{safeUserSelection}</div>
          </div>

          <div className="preset-field">
            <h3 className="text-heading">IRIS Selection</h3>
            <div className="preset-value">{safeIrisSelection}</div>
          </div>

          <div className="preset-field">
            <h3 className="text-heading">IRIS Explanation</h3>
            <div className="preset-value iris-explanation">{safeIrisExplanation}</div>
          </div>

          <div className="explanation-field">
            <div className="explanation-header">
              <p className="explanation-prompt">Please explain why you changed the preset</p>
            </div>
            
            <textarea
              value={userExplanation}
              onChange={handleTextChange}
              className={`explanation-textarea ${inputErrorClass}`}
              placeholder="Enter your explanation here..."
            />
            
            {validationError && (
              <div className="validation-error">
                <p className="error-message">{validationError.message}</p>
              </div>
            )}
          </div>

          <div className="preset-change-popup-buttons">
            {isProcessing ? (
              <div className="processing-indicator">
                <i className="fas fa-exclamation-circle"></i> Processing
              </div>
            ) : saveStatus === 'success' ? (
              <div className="success-indicator">
                <i className="fas fa-check-circle"></i> Saved
              </div>
            ) : saveStatus === 'force_success' ? (
              <div className="success-indicator">
                <i className="fas fa-check-circle"></i> Auto-approved after 5 attempts
              </div>
            ) : saveStatus === 'error' ? (
              <div className="error-indicator">
                <i className="fas fa-times-circle"></i> Error Saving
              </div>
            ) : (
              <>
                <button 
                  className="cancel-button" 
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button 
                  className="save-button" 
                  onClick={handleSave}
                  disabled={!userExplanation.trim()}
                >
                  {attemptCount > MAX_ATTEMPTS ? 'Submit Final Attempt' : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PresetChangePopup;
